"use client";

import ContactList from "./ContactList";
import { useCallback, useEffect, useState, useRef } from "react";
import {
  Box,
  Divider,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import {
  Tabs,
  TabList,
  Tab,
  SplitButton,
  ListFilterBar,
  ResultCount,
} from "@/ui";
import type { Filter, FilterFieldConfig } from "@/ui/components";
import { BuildingIcon, TagIcon, UserIcon } from "lucide-react";
import { CSVBoxButton } from "@csvbox/react";
import toast from "react-hot-toast";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import config from "@lib/config";
import useRecruiterDetails from "@lib/user/useRecruiterDetails";
import ContactDetailsDialog from "./parts/ContactDetailsDialog";
import PageTitle from "@components/shared/Typography/PageTitle";
import Paging from "@components/shared/table/Paging";
import useSearch from "@lib/shared/useSearch";
import useUrlListState, { useUrlSync } from "@lib/shared/useUrlListState";
import { CONTACT_SORT_OPTIONS, TAG_TYPE } from "@lib/enums";
import useContacts from "@lib/contacts/useContacts";
import useContactDetails from "@lib/contacts/useContactDetails";
import useContactNames from "@lib/contacts/useContactNames";
import useContactCompanies from "@lib/contacts/useContactCompanies";
import useTags from "@lib/tags/useTags";

const Container = styled(Box)(({ theme }) => ({
  padding: "40px 46px 40px 46px",

  [theme.breakpoints.down("md")]: {
    padding: "30px 20px",
  },
}));

interface ContactRefs {
  id: number;
  uuid?: string | null;
}

/** What goes in ?contact=: the uuid, or the id for rows from an API without one. */
const contactRef = (contact: ContactRefs) => contact.uuid ?? String(contact.id);

/** Whether a ?contact= value refers to this contact, by uuid or legacy numeric id. */
const matchesRef = (contact: ContactRefs, ref: string) =>
  contact.uuid === ref || String(contact.id) === ref;

export default function Contacts() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { recruiter } = useRecruiterDetails();
  const hasTeamSharing = recruiter?.user?.has_team_sharing ?? false;

  // Built as a variable so the untyped hook's narrowly-inferred options type
  // doesn't reject the extra keys it does read at runtime.
  const listStateOptions = {
    defaultSortProperty: CONTACT_SORT_OPTIONS.UPDATED_AT,
    defaultSortDirection: "desc",
  };
  const { initial, updateUrl } = useUrlListState(listStateOptions);

  const [page, setPage] = useState(initial.page);
  const [activeFilters, setActiveFilters] = useState<Filter<string>[]>(
    initial.filters,
  );
  const [searchFilters, setSearchFilters] = useState<{ company?: string }>({});
  const [sortBy, setSortBy] = useState(initial.sortBy);

  const { names } = useContactNames();
  const { companies } = useContactCompanies();

  const handleSuggestionSelect = (value: string): boolean => {
    if (companies.includes(value)) {
      setSearchFilters({ company: value });
      setPage(1);
      return true;
    }
    // Partial company match — pass raw text; API does LIKE '%value%' for single values
    const hasPartialMatch = companies.some((c: string) =>
      c.toLowerCase().includes(value.toLowerCase()),
    );
    if (hasPartialMatch) {
      setSearchFilters({ company: value });
      setPage(1);
      return true;
    }
    return false;
  };

  // Passed as a variable for the same reason as listStateOptions above.
  const searchOptions = {
    placeholder: "Search contacts...",
    helperText: "Search by name or company",
    submitButton: true,
    suggestions: [...names, ...companies],
    onSuggestionSelect: handleSuggestionSelect,
    initialValue: initial.search,
  };
  const { SearchField, search, searchInput } = useSearch(setPage, searchOptions);

  useUrlSync(updateUrl, { page, sortBy, search, filters: activeFilters });

  // Clear the company filter when the search box is emptied. Handled during
  // render (React's "You Might Not Need an Effect") rather than in an effect.
  const [prevSearchInput, setPrevSearchInput] = useState(searchInput);
  if (searchInput !== prevSearchInput) {
    setPrevSearchInput(searchInput);
    if (!searchInput) setSearchFilters({});
  }

  const { tags } = useTags({
    page: 1,
    sortBy: { property: "name", direction: "asc" },
    search: "",
    filters: { with_default: true, unique: "slug", type: TAG_TYPE.CONTACT },
  });

  const filterFields: FilterFieldConfig<string>[] = [
    {
      key: "name",
      label: "Name",
      type: "multiselect",
      icon: <UserIcon size={14} />,
      searchable: true,
      options: names.map((n: string) => ({ value: n, label: n })),
    },
    {
      key: "company",
      label: "Company",
      type: "multiselect",
      icon: <BuildingIcon size={14} />,
      searchable: true,
      options: companies.map((c: string) => ({ value: c, label: c })),
    },
    {
      key: "tags",
      label: "Tags",
      type: "multiselect",
      icon: <TagIcon size={14} />,
      options: (tags ?? []).map((tag: { name: string }) => ({
        value: tag.name,
        label: tag.name,
      })),
    },
  ];

  const getFilterValues = (field: string): string[] =>
    activeFilters.find((f) => f.field === field)?.values ?? [];

  const handleFiltersChange = (newFilters: Filter<string>[]) => {
    setActiveFilters(newFilters);
    setPage(1);
  };

  const filteredTagNames = activeFilters
    .filter((f) => f.field === "tags")
    .flatMap((f) => f.values);

  const filteredNames = getFilterValues("name");
  const filteredCompanies = getFilterValues("company");
  const allCompanies = [...filteredCompanies, ...(searchFilters.company ? [searchFilters.company] : [])];

  const activeApiFilters = {
    source: "floats",
    ...(filteredTagNames.length > 0 ? { tags: filteredTagNames } : {}),
    ...(filteredNames.length > 0 ? { name: filteredNames.join("|") } : {}),
    ...(allCompanies.length > 0 ? { company: allCompanies.join("|") } : {}),
  };

  // My Contacts only. The untyped hook infers `filters` as an array from its
  // default, but at runtime it takes the keyed object built above.
  const { contacts, lastPage, total, from, to, hasMore, contactsIsLoading, invalidate } =
    useContacts({
      page,
      sortBy,
      search,
      filters: activeApiFilters,
    } as unknown as Parameters<typeof useContacts>[0]);

  // Adding a contact is a local dialog; editing one lives in the URL
  // (?contact=<uuid>) so the sheet is shareable, survives a reload, and closes
  // on Back instead of dropping the user out of the list.
  const [addOpen, setAddOpen] = useState(false);
  const contactParam = searchParams.get("contact");

  // Dynamic contact record passed straight through to the details dialog; its
  // shape is owned by ContactList/ContactDetailsDialog, not re-declared here.
  // Deliberately kept after the sheet closes, so the dialog doesn't swap from
  // the edit sheet to the add dialog shell mid exit-animation.
  const [contact, setContact] = useState<any>(null);
  const contactIsOpen =
    contactParam !== null && contact !== null && matchesRef(contact, contactParam);

  // Whether *we* pushed the entry we're sitting on. If the user arrived on a
  // ?contact= link directly there's nothing to go back to, so closing has to
  // strip the param instead of walking out of the app.
  const pushedRef = useRef(false);

  const buildHref = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const params = new URLSearchParams(searchParams);
      mutate(params);
      const qs = params.toString();
      return qs ? `${pathname}?${qs}` : pathname;
    },
    [pathname, searchParams],
  );

  const closeContact = useCallback(() => {
    if (pushedRef.current) {
      pushedRef.current = false;
      router.back();
      return;
    }
    router.replace(buildHref((params) => params.delete("contact")), { scroll: false });
  }, [buildHref, router]);

  // Dismissing via the browser's own Back button closes the sheet without
  // going through closeContact, so clear the flag whenever it ends up shut.
  useEffect(() => {
    if (!contactParam) pushedRef.current = false;
  }, [contactParam]);

  // A deep link to a contact that isn't the one already in hand (e.g. it's on
  // another page of results) is fetched by uuid, or by id for older links.
  const {
    contact: linkedContact,
    error: linkedContactError,
    isInvalidRef,
  } = useContactDetails(contactParam, {
    enabled: contactParam !== null && !contactIsOpen,
  });

  // Copy the fetched contact in once, rather than deriving it every render: a
  // refetch hands back a new object, which the dialog would treat as a new
  // contact and reset any edits in progress.
  if (
    linkedContact &&
    contactParam !== null &&
    matchesRef(linkedContact, contactParam) &&
    !contactIsOpen
  ) {
    setContact(linkedContact);
  }

  // Older links (/contacts/<id>, report embeds) carry the numeric id; swap it
  // for the uuid once we have it so the URL people copy is the canonical one.
  useEffect(() => {
    if (contactIsOpen && contact.uuid && contactParam !== contact.uuid) {
      router.replace(buildHref((params) => params.set("contact", contact.uuid)), {
        scroll: false,
      });
    }
  }, [contactIsOpen, contact, contactParam, buildHref, router]);

  // Not found, not ours, or not a contact ref: drop the param (handleError has
  // already told the user about any failed fetch).
  useEffect(() => {
    if (linkedContactError || isInvalidRef) closeContact();
  }, [linkedContactError, isInvalidRef, closeContact]);

  const csvLaunchRef = useRef<(() => void) | null>(null);

  const csvUser = { user_id: recruiter?.id, email: recruiter?.user?.email };

  const handleCsvImport = (
    result: boolean,
    data: { row_success: number },
  ) => {
    if (result) {
      toast.success(`Importing ${data.row_success} contacts! The new contacts should be visible within a few minutes.`, { duration: 8000 });
      refresh();
    } else {
      toast.error("Import failed. Please try again.");
    }
  };

  const splitItems = [
    { label: "Add manually", onClick: () => openAddDialog() },
    { label: "Import CSV", onClick: () => csvLaunchRef.current?.() },
  ];

  const contactsIcon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <circle cx="8" cy="7" r="3.5" />
      <path d="M2 21v-1a6 6 0 0112 0v1" />
      <path d="M16 3.13a4 4 0 010 7.75" />
      <path d="M22 21v-1a4 4 0 00-3-3.87" />
    </svg>
  );

  const openAddDialog = () => {
    setContact(null);
    setAddOpen(true);
  };

  const openEditDialog = (editContact: any) => {
    setContact(editContact);
    pushedRef.current = true;
    router.push(buildHref((params) => params.set("contact", contactRef(editContact))), {
      scroll: false,
    });
  };

  const refresh = () => {
    setTimeout(() => {
      invalidate();
    }, 3000);
  };

  const handleTabChange = (value: string) => {
    if (value === "team") router.push("/team/contacts");
  };

  return (
    <Box>
      <Container>
          <Stack
            sx={{ mb: 2 }}
            direction="row"
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <PageTitle small={false}>Contacts</PageTitle>
            <Box display={"flex"} columnGap={"4px"} alignItems="center">
              <div style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}>
                <CSVBoxButton
                  licenseKey={config.csvboxContactSheetLicenceKey}
                  user={csvUser}
                  onImport={handleCsvImport}
                  render={(launch: () => void) => { csvLaunchRef.current = launch; return <span />; }}
                >
                  csv
                </CSVBoxButton>
              </div>
              <SplitButton
                label="Add contacts"
                startIcon={contactsIcon}
                size="md"
                variant="accent"
                items={splitItems}
                mainOpensMenu
              />
            </Box>
          </Stack>
          <Box sx={{ mb: 6, width: "60%" }}>
            <Typography variant="body2">
              Your contacts are the people within the companies that you work
              with. You can add new contacts manually or import them from a
              spreadsheet.
            </Typography>
          </Box>
          {hasTeamSharing ? (
            <Tabs value="mine" onChange={handleTabChange}>
              <TabList>
                <Tab value="mine">My contacts</Tab>
                <Tab value="team">Team contacts</Tab>
              </TabList>
            </Tabs>
          ) : (
            <Divider />
          )}
          <Box sx={{ my: 4 }}>
            <Box sx={{ width: '50%', mb: 2 }}>{SearchField}</Box>
            <ListFilterBar
              fields={filterFields}
              filters={activeFilters}
              onChange={handleFiltersChange}
            />
          </Box>
          {!contactsIsLoading && contacts?.length > 0 && (
            <ResultCount
              from={from}
              to={to}
              total={total}
              noun="contact"
            />
          )}
          <ContactList
            contacts={contacts}
            sortBy={sortBy as { property: string; direction: "asc" | "desc" }}
            setSortBy={setSortBy}
            openEditDialog={openEditDialog}
            isLoading={contactsIsLoading}
          />
          <Paging
            page={page}
            lastPage={lastPage}
            setPage={setPage}
            hasMore={hasMore}
          />
          <ContactDetailsDialog
            contactToEdit={contact}
            open={addOpen || contactIsOpen}
            onClose={() => (addOpen ? setAddOpen(false) : closeContact())}
            recruiterId={recruiter?.id}
          />
        </Container>
    </Box>
  );
}
